import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchRemoteListSales } from '../../reports/infra/reportsRemoteSalesListRepo';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import type { ReportsQueryScope } from '../../reports/domain/reportsQueryScope';
import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir, SaleCommissionFilter } from '../domain/salesTypes'
import type { SaleChannelFilter } from '../infra/salesListFilters';
import type { SaleListRow } from '../domain/salesTypes';
import { SALES_LIST_PAGE_SIZE, SALES_LIST_PAGE_SIZE_OPTIONS } from '../infra/salesList.constants'
import type { SalesListPageSize } from '../infra/salesList.constants';
import { withSalesListRemoteTimeout } from './salesListLoadHelpers';

export type UseSalesListForBranchOptions = {
  branchId: string | null | undefined;
  /** Se invoca en cada carga para que rangos tipo “hoy” usen la hora actual. */
  getDateRange: () => { fromIso: string; toIso: string };
  search: string;
  /** Por defecto mismo tamaño de página que el catálogo POS. */
  pageSize?: SalesListPageSize;
  /** Filtro por operador; omitir para listar todos. */
  cashierUserIdFilter?: string;
  /** Filtro Atendió (`primary_staff_user_id`); omitir para listar todos. */
  primaryStaffUserIdFilter?: string;
  /** Solo ventas con cobro pendiente (Informes drill-down). */
  paymentPendingOnly?: boolean;
  /** Filtro por estado de venta (activa/anulada). */
  statusFilter?: SaleStatusFilter;
  /** Filtro por medio de cobro (method_id). */
  paymentMethodFilter?: string;
  /** Filtro por estado fiscal. */
  fiscalStatusFilter?: SaleFiscalStatusFilter;
  /** Canal mostrador / online. */
  channelFilter?: SaleChannelFilter;
  /** Ventas con/sin comisión (server-side). `all` = sin filtro. */
  commissionFilter?: SaleCommissionFilter;
  /** Columna de ordenamiento. */
  sortKey?: SaleSortKey;
  /** Dirección de ordenamiento. */
  sortDir?: SaleSortDir;
  /** Alcance Informes para alinear listado con KPIs filtrados. */
  queryScope?: ReportsQueryScope | null;
};

/**
 * Listado de ventas por sucursal con paginado (Anterior / Siguiente), como el catálogo POS.
 * Online intenta RPC primero e incorpora ventas locales del rango ausentes en remoto (página 0).
 */
export function useSalesListForBranch({
  branchId,
  getDateRange,
  search,
  pageSize: pageSizeOption,
  cashierUserIdFilter,
  primaryStaffUserIdFilter,
  paymentPendingOnly,
  queryScope,
  statusFilter,
  paymentMethodFilter,
  fiscalStatusFilter,
  channelFilter,
  commissionFilter,
  sortKey,
  sortDir,
}: UseSalesListForBranchOptions) {
  const [pageSize, setPageSizeState] = useState<SalesListPageSize>(pageSizeOption ?? SALES_LIST_PAGE_SIZE);
  const [rows, setRows] = useState<SaleListRow[]>([]);
  const [loading, setLoading] = useState(() => Boolean(branchId));
  const [hasMore, setHasMore] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const setPageSize = useCallback((size: SalesListPageSize) => {
    if (!SALES_LIST_PAGE_SIZE_OPTIONS.includes(size)) return;
    setPageSizeState((prev) => {
      if (prev === size) return prev;
      return size;
    });
    setPageIndex(0);
  }, []);

  const listRequestRef = useRef(0);
  const loadPageRef = useRef<((targetPageIndex: number) => Promise<void>) | null>(null);

  const resolvedBranchId = branchId ?? null;

  const [debouncedSearch, setDebouncedSearch] = useState(() => (search ?? '').trim());
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((search ?? '').trim()), 320);
    return () => clearTimeout(t);
  }, [search]);

  const dateRangeKey = useMemo(() => {
    const { fromIso, toIso } = getDateRange();
    return `${fromIso}|${toIso}`;
  }, [getDateRange]);

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        pageSize,
        cashierUserIdFilter,
        primaryStaffUserIdFilter,
        paymentPendingOnly,
        queryScope,
        statusFilter,
        paymentMethodFilter,
        fiscalStatusFilter,
        channelFilter,
        commissionFilter,
        sortKey,
        sortDir,
      }),
    [
      pageSize,
      cashierUserIdFilter,
      primaryStaffUserIdFilter,
      paymentPendingOnly,
      queryScope,
      statusFilter,
      paymentMethodFilter,
      fiscalStatusFilter,
      channelFilter,
      commissionFilter,
      sortKey,
      sortDir,
    ],
  );

  const loadPage = useCallback(
    async (targetPageIndex: number) => {
      if (!resolvedBranchId) {
        setRows([]);
        setHasMore(false);
        setPageIndex(0);
        return;
      }

      const requestId = listRequestRef.current + 1;
      listRequestRef.current = requestId;

      const { fromIso, toIso } = getDateRange();
      const searchTrim = debouncedSearch;
      const offset = targetPageIndex * pageSize;

      setLoading(true);
      setLoadError(false);
      try {
        const online = await isOnline();
        if (online) {
          const remote = await withSalesListRemoteTimeout(
            fetchRemoteListSales({
              branchId: resolvedBranchId,
              fromIso,
              toIso,
              search: searchTrim || undefined,
              limit: pageSize,
              offset,
              cashierUserIdFilter,
              primaryStaffUserIdFilter,
              paymentPendingOnly,
              queryScope,
              statusFilter,
              paymentMethodFilter,
              fiscalStatusFilter,
              channelFilter,
              commissionFilter,
              sortKey,
              sortDir,
            }),
            null,
          );
          if (requestId !== listRequestRef.current) return;

          if (remote) {
            let mergedRows = remote.rows;
            let mergedHasMore = remote.hasMore;

            // ADR-017 / legacy-noop: no merge local — SQLite retirado; listSales local throw.
            setRows(mergedRows);
            setHasMore(mergedHasMore);
            setPageIndex(targetPageIndex);
            setTotalCount(remote.totalCount);
            return;
          }
        }

        if (requestId !== listRequestRef.current) return;

        // Offline / remote null: no inventar lista vacía como “sin ventas”.
        setRows([]);
        setHasMore(false);
        setPageIndex(0);
        setLoadError(true);
      } catch (e) {
        console.warn('[useSalesListForBranch] load', e);
        if (requestId !== listRequestRef.current) return;
        setRows([]);
        setHasMore(false);
        setPageIndex(0);
        setLoadError(true);
      } finally {
        if (requestId === listRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [resolvedBranchId, getDateRange, debouncedSearch, pageSize, cashierUserIdFilter, primaryStaffUserIdFilter, paymentPendingOnly, queryScope, statusFilter, paymentMethodFilter, fiscalStatusFilter, channelFilter, commissionFilter, sortKey, sortDir]
  );

  loadPageRef.current = loadPage;

  useEffect(() => {
    if (!resolvedBranchId) {
      setLoading(false);
      return;
    }
    setRows([]);
    void loadPageRef.current?.(0);
  }, [resolvedBranchId, dateRangeKey, debouncedSearch, filtersKey]);

  const goToNextPage = useCallback(() => {
    if (!hasMore || loading) return;
    void loadPage(pageIndex + 1);
  }, [pageIndex, hasMore, loading, loadPage]);

  const goToPreviousPage = useCallback(() => {
    if (pageIndex === 0 || loading) return;
    void loadPage(pageIndex - 1);
  }, [pageIndex, loading, loadPage]);

  const goToPage = useCallback(
    (targetPageIndex: number) => {
      if (loading) return;
      const maxPage =
        totalCount != null && totalCount > 0 ? Math.ceil(totalCount / pageSize) - 1 : null;
      const clamped = Math.max(0, maxPage != null ? Math.min(targetPageIndex, maxPage) : targetPageIndex);
      if (clamped === pageIndex) return;
      void loadPage(clamped);
    },
    [loading, totalCount, pageSize, pageIndex, loadPage]
  );

  const reload = useCallback(() => loadPage(0), [loadPage]);

  return {
    rows,
    loading,
    hasMore,
    pageIndex,
    totalCount,
    loadError,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    reload,
    pageSize,
    setPageSize,
  };
}
